// Components/Tabs/DeleteLabTab.jsx

import React from "react";
import PropTypes from "prop-types";

// Importing components
import Slider from "../Slider/Slider";
import SliderForm from "../SliderForm/SliderForm.jsx";
import useAdminToast from "../useAdminToast";
import Backend from "../../../utils/api.js";
import { useDocumentTitle } from "../../../utils/useDocumentTitle";

/**
 * DeleteLabTab Component
 * 
 * This component provides a confirmation form for deleting a lab. It ensures that the user
 * intentionally wants to delete the lab by requiring them to type "confirm". Upon successful
 * deletion, it returns to the space picker and shows a toast.
 * 
 * @param {Object} props - Component properties.
 * @param {Object} props.user - The current user object containing user details.
 * @param {string} props.labId - The unique identifier of the lab to be deleted.
 * @param {Function} props.pushTab - Function to push a new tab onto the stack.
 * @param {Function} props.revert - Function to revert the tab stack by a specified number of steps.
 */
function DeleteLabTab({ user, labId, pushTab, revert, onLabChanged }) {
  
  const toast = useAdminToast();
  useDocumentTitle("Admin Delete space Tab – Pinpoint");
  return (
    <Slider
      name="Delete space"
      navColor="rgb(255, 238, 83)"
      onClick={() => revert(1)} // Revert one step when navigation bar is clicked
      index={0} // Starting index for the delete lab tab
      slide={true}
    >
      {/* Warning Message */}
      <p className="admin-warning-copy">
        Deleted spaces can only be recovered by a Pinpoint developer under special circumstances. Data is not immediately lost, however
        we reserve the right to remove it at any point after a space is deleted to preserve storage.
      </p>
      
      {/* Deletion Confirmation Form */}
      <SliderForm
        title="Delete"
        fields={[
          {
            key: "name",
            placeholder: 'Type "confirm" to delete space',
            validate: /^confirm$/, // Regex to ensure exact match
            errorText: 'You must type "confirm" to delete a space.',
          },
        ]}
        onSubmit={async (x) => {
          if (x.name === "confirm") {
            // Proceed to delete the lab via backend API
            return Backend.toggleLab({
              id: labId,
              UID: user.UID,
              disable: true,
            }).then((res) => {
              if (res.status !== 201) throw new Error("Could not delete this space. Try again.");
              onLabChanged?.();
              toast({ title: "Space deleted" });
              revert(100);
            });
          } else {
            throw new Error('Type "confirm" to delete this space.');
          }
        }}
        deleteForm={true} // Custom prop to indicate this is a delete form
      />
    </Slider>
  );
}

// PropTypes for type-checking component props
DeleteLabTab.propTypes = {
  user: PropTypes.object.isRequired,
  labId: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
  onLabChanged: PropTypes.func,
};

export default DeleteLabTab;
